import { EndUserCertificate, EndUserConstants, EndUserError } from "../euscp/euscp";

/**
 * Загрузить данные по ссылке
 * @param {string} url - ссылка на скачивание данных
 * @param {string?} dataType - тип данных, которые необходимо загрузить. Возможные значения: binary, json и пусто для строковых данных
 * @returns {Promise<string | Uint8Array | object>} загруженные данные
 */
export function downloadData(url, dataType, token, postData) {
  return new Promise((resolve, reject) => {
    url = makeUrl(url);

    const xmlHttp = new XMLHttpRequest();

    xmlHttp.onerror = function (e) {
      console.error(e);
      reject("Download data error. URL - " +
        url + ", status - " + xmlHttp.status);
    };

    xmlHttp.onload = function () {
      if (xmlHttp.readyState != XMLHttpRequest.DONE)
        return;

      const XMLHttpStatusDone = 200;
      if (xmlHttp.status != XMLHttpStatusDone) {
        reject("Download data error. URL - " +
          url + ", status - " + xmlHttp.status);
        return;
      }
      let blob;
      switch (dataType) {
        case "binary":
          resolve(new Uint8Array(xmlHttp.response));
          break;
        case "json":
          resolve(JSON.parse(xmlHttp.responseText.replace(/\\'/g, "'")));
          break;
        case "xml":
          resolve(xmlHttp.responseXML);
          break;
        case "blob":
          try {
            blob = new Blob([xmlHttp.responseText], { type: "application/javascript" });
          } catch (e) { // Backwards-compatibility
            const BlobBuilder = window.BlobBuilder || window.WebKitBlobBuilder || window.MozBlobBuilder || window.MSBlobBuilder;
            blob = new BlobBuilder();
            blob.append(xmlHttp.responseText);
            blob = blob.getBlob();
          }
          resolve(blob);
          break;
        default:
          resolve(xmlHttp.responseText);
          break;
      }
    };
    if (dataType === "binary") {
      xmlHttp.responseType = "arraybuffer";
    } else if (dataType == "xml") {
      xmlHttp.responseType = "document";
    }
    xmlHttp.open(postData ? "POST" : "GET", url, true);
    if (typeof token === "string") {
      xmlHttp.setRequestHeader("Authorization", "Bearer " + token);
    }

    if (postData) {
      const jsonData = JSON.stringify(postData);
      xmlHttp.setRequestHeader("Content-Type", "application/json");
      xmlHttp.send(jsonData);
    }
    else {
      xmlHttp.send();
    }
  });
}

export function makeUrl(url) {
  const origin = location.origin,
    pathname = location.pathname;

  if (url.indexOf("http://") === 0 || url.indexOf("https://") === 0) {
    return url;
  }
  if (url.indexOf("/") === 0 && origin) {
    return origin + url;
  }
  if (origin && pathname) {
    return origin + pathname.substr(0, pathname.lastIndexOf("/")) + "/" + url;
  }
  return url;
}

export function format(format) {
  const args = Array.prototype.slice.call(arguments, 1);
  return format.replace(/{(\d+)}/g, function (match, number) {
    return typeof args[number] != "undefined" ? args[number] : match;
  });
}

export function readFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = function () {
      if (this.readyState != this.DONE) {
        reject(this.error);
      } else {
        resolve(new Uint8Array(this.result));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

export function arrayToBase64(array) {
  let base64Str = "";
  const m_map = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

  const bytes = new Uint8Array(array);
  const bytesLength = bytes.length;

  /* eslint-disable no-magic-numbers */
  for (let i = 0; i < bytesLength; i += 3) {
    base64Str += m_map[bytes[i] >> 2];
    base64Str += m_map[(bytes[i] & 3) << 4 | bytes[i + 1] >> 4];
    base64Str += m_map[(bytes[i + 1] & 15) << 2 | bytes[i + 2] >> 6];
    base64Str += m_map[bytes[i + 2] & 63];
  }

  if (bytesLength % 3 === 2) {
    base64Str = base64Str.substring(0, base64Str.length - 1) + "=";
  } else if (bytesLength % 3 === 1) {
    base64Str = base64Str.substring(0, base64Str.length - 2) + "==";
  }
  /* eslint-enable no-magic-numbers */

  return base64Str;
}

export function base64ToArray(str) {
  const raw = window.atob(str);
  const rawLength = raw.length;
  const array = new Uint8Array(new ArrayBuffer(rawLength));

  for (let i = 0; i < rawLength; i++) {
    array[i] = raw.charCodeAt(i);
  }
  return array;
}

export function stringToArray(s) {
	const utf8 = unescape(encodeURIComponent(s));

	const arr = [];
	for (let i = 0; i < utf8.length; i++) {
		arr.push(utf8.charCodeAt(i));
	}
	return arr;
}

export function arrayToString(arr) {
	const ret = [];

	let length = arr.length;
	if (length > 0 &&
		arr[length - 1] == 0) {
		length -= 1;
	}

  /* eslint-disable no-magic-numbers */
	for (let i = 0; i < length; i++) {
		let chr = arr[i];
		if (chr > 0xFF) {
			chr &= 0xFF;
		}

		ret.push(String.fromCharCode(chr));
	}
  /* eslint-enable no-magic-numbers */

	return decodeURIComponent(escape(ret.join("")));
}

export function convertToBase64(str) {
  return arrayToBase64(stringToArray(str));
}

export function convertFromBase64(str) {
  return arrayToString(base64ToArray(str));
}

/**
 * Получить алгоритм хеширования для алгоритма подписи
 * @param {EndUserConstants.EndUserSignAlgo} signAlgo - Алгоритм подписи
 * @returns {EndUserConstants.EndUserHashAlgo} Алгоритм хеширования
 */
export function signAlgoToHashAlgo(signAlgo) {
  switch (signAlgo) {
    case EndUserConstants.EndUserSignAlgo.RSAWithSHA:
    case EndUserConstants.EndUserSignAlgo.ECDSAWithSHA:
      return EndUserConstants.EndUserHashAlgo.SHA256;
    case EndUserConstants.EndUserSignAlgo.DSTU4145WithGOST34311:
      return EndUserConstants.EndUserHashAlgo.GOST34311;
    case EndUserConstants.EndUserSignAlgo.DSTU4145WithDSTU7564:
      return EndUserConstants.EndUserHashAlgo.DSTU7564_256;
    default:
      return EndUserConstants.EndUserHashAlgo.Unknown;
  }
}

/**
 * Получить список доступных алгоритмов подписи считанного ключа
 * @param {Array<EndUserCertificate>} certificates
 * @returns {Array<EndUserConstants.EndUserSignAlgo>} список доступных алгоритмов подписи
 */
export function getSupportedSignAlgos(certificates, glsign = null) {
  const signAlgos = [];
  certificates.forEach(certificate => {
    if ((certificate.infoEx.keyUsageType & EndUserConstants.EndUserKeyUsage.DigitalSignature) !== EndUserConstants.EndUserKeyUsage.DigitalSignature) {
      return;
    }
    switch (certificate.infoEx.publicKeyType) {
      case EndUserConstants.EndUserCertKeyType.DSTU4145:
        if ((certificate.infoEx.certHashType === EndUserConstants.EU_CERT_HASH_TYPE_GOST34311 || certificate.infoEx.certHashType === EndUserConstants.EU_CERT_HASH_TYPE_DSTU7564_256)
          && glsign?.HashAlgorithm === "GOST34311") {
          signAlgos.push(EndUserConstants.EndUserSignAlgo.DSTU4145WithGOST34311);
        } else {
          signAlgos.push(EndUserConstants.EndUserSignAlgo.DSTU4145WithDSTU7564);
        }
        break;
      case EndUserConstants.EndUserCertKeyType.RSA:
        signAlgos.push(EndUserConstants.EndUserSignAlgo.RSAWithSHA);
        break;
      case EndUserConstants.EndUserCertKeyType.ECDSA:
        signAlgos.push(EndUserConstants.EndUserSignAlgo.ECDSAWithSHA);
        break;
    }
  });
  return signAlgos;
}

export function isUrlValid(url) {
  try {
    return !!new URL(url);
  } catch {
    return false;
  }
}

export function getFileNameFromUrl(url) {
  url = new URL(url);
  return url.searchParams.get("file") || url.searchParams.get("name") || url.pathname.split("/").pop();
}

export async function downloadAndSignFiles(fileUrl, signFunc) {
  const isParamValid = (url) => typeof url === "string" && isUrlValid(url) || typeof url === "object" && typeof url.name === "string" && typeof url.val === "string" && isUrlValid(url.val);

  if (Array.isArray(fileUrl)) {
    if (!fileUrl.every(isParamValid) || !(fileUrl.every(url => typeof url === "string") || fileUrl.every(url => typeof url === "object"))) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: "Bad parameter fileUrl"
      };
    }
    const returnNamedData = fileUrl.every(url => typeof url === "object");

    fileUrl = await Promise.all(fileUrl.map(async (url) => typeof url === "object"
      ? { name: url.name, val: await downloadData(url.val, "binary") }
      : { name: getFileNameFromUrl(url), val: await downloadData(url, "binary") }));

    const signs = await signFunc(fileUrl);
    return returnNamedData ? signs : signs.map(sign => sign.val || sign);
  } else {
    if (!isParamValid(fileUrl)) {
      throw {
        code: EndUserError.EU_ERROR_BAD_PARAMETER,
        message: "Bad parameter fileUrl"
      };
    }

    const returnNamedData = typeof fileUrl === "object";

    fileUrl = typeof fileUrl === "object"
      ? { name: fileUrl.name, val: await downloadData(fileUrl.val, "binary") }
      : { name: getFileNameFromUrl(fileUrl), val: await downloadData(fileUrl, "binary") };

    const sign = await signFunc(fileUrl);
    return returnNamedData ? sign : sign.val || sign;
  }
}

export function isDefaultValue(value) {
  return value === false || value === null || typeof value === "undefined";
}

// отсчёт месяцев в JS начинается с нуля -_-
// реальная дата - 01.07.2026
/* eslint-disable no-magic-numbers */
export const IS_DSTU_7564_DAY = () => Date.now() >= new Date(2026, 6, 1);

export function dateWithoutTimezone(date) {
  const tzoffset = date.getTimezoneOffset() * 60000; //offset in milliseconds
  const withoutTimezone = new Date(date.valueOf() - tzoffset)
    .toISOString()
    .slice(0, -1);
  return withoutTimezone;
};
